// Create task (§11 `/tasks/new`): the publish form. Human-chosen `id` (upsert key, invariant #1),
// service + target, schedule (cron/interval/one-shot), timezone, payload JSON, optional callbacks.
// Sub-minute cron is auto-promoted to the do_alarm engine server-side (shown as a live hint here).

import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Api, ApiError, type ScheduleKind, type TargetType } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { composeUrl } from "../lib/format";
import { Card, ErrorBox, Field, Spinner } from "../components/ui";

const COMMON_TZ = [
  "UTC",
  "America/Chicago",
  "America/New_York",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
];

/** Mirror of the admin worker's sub-minute detection (every Ns with N<60, or 6-field cron). */
function isSubMinute(kind: ScheduleKind, cron: string, intervalSeconds: number): boolean {
  if (kind === "interval") return intervalSeconds > 0 && intervalSeconds < 60;
  if (kind === "cron") {
    const fields = cron.trim().split(/\s+/);
    return fields.length >= 6; // seconds field present
  }
  return false;
}

export function TaskNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const services = useAsync(() => Api.listServices(), []);

  const [id, setId] = useState("");
  const [serviceId, setServiceId] = useState(params.get("serviceId") ?? "");
  const [targetType, setTargetType] = useState<TargetType>("http");
  const [path, setPath] = useState("");
  const [method, setMethod] = useState("POST");
  const [targetGroupId, setTargetGroupId] = useState("");
  const [scheduleKind, setScheduleKind] = useState<ScheduleKind>("cron");
  const [cron, setCron] = useState("0 * * * *");
  const [intervalSeconds, setIntervalSeconds] = useState(60);
  const [notBefore, setNotBefore] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [payload, setPayload] = useState("{}");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [failureCallbackUrl, setFailureCallbackUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [payloadError, setPayloadError] = useState<string | null>(null);

  const selectedService = services.data?.data.find((s) => s.id === serviceId);
  const urlPreview = useMemo(
    () => (selectedService ? composeUrl(selectedService.baseUrl, path) : ""),
    [selectedService, path],
  );
  const subMinute = isSubMinute(scheduleKind, cron, intervalSeconds);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPayloadError(null);

    let parsedPayload: unknown = undefined;
    if (payload.trim()) {
      try {
        parsedPayload = JSON.parse(payload);
      } catch {
        setPayloadError("Payload must be valid JSON.");
        return;
      }
    }

    const body: Record<string, unknown> = {
      id: id.trim(),
      serviceId,
      targetType,
      method,
      scheduleKind,
      timezone,
      payload: parsedPayload,
    };
    if (targetType === "http") body.path = path;
    if (targetType === "group") body.targetGroupId = targetGroupId;
    if (scheduleKind === "cron") body.cron = cron;
    if (scheduleKind === "interval") body.intervalSeconds = intervalSeconds;
    if (scheduleKind === "one_shot" && notBefore) body.notBefore = new Date(notBefore).getTime();
    if (callbackUrl) body.callbackUrl = callbackUrl;
    if (failureCallbackUrl) body.failureCallbackUrl = failureCallbackUrl;

    setSubmitting(true);
    try {
      const task = await Api.upsertTask(body);
      navigate(`/tasks/${task.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? new Error(`${err.message}${err.details ? ` — ${JSON.stringify(err.details)}` : ""}`)
          : err instanceof Error
            ? err
            : new Error(String(err)),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (services.loading && !services.data) return <Spinner />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-xl font-semibold text-white">New task</h1>
      {error && <ErrorBox error={error} />}

      <form onSubmit={submit} className="space-y-4">
        <Card title="Identity & target">
          <Field label="Task ID" hint="Human-chosen, stable. Re-publishing the same ID upserts the task.">
            <input className="input" value={id} onChange={(e) => setId(e.target.value)} required placeholder="daily-report" />
          </Field>

          <Field label="Service">
            <select className="input" value={serviceId} onChange={(e) => setServiceId(e.target.value)} required>
              <option value="">Select a service…</option>
              {services.data?.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} — {s.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Target type">
            <select className="input" value={targetType} onChange={(e) => setTargetType(e.target.value as TargetType)}>
              <option value="http">http</option>
              <option value="group">group</option>
              <option value="workflow">workflow</option>
            </select>
          </Field>

          {targetType === "http" && (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Field label="Method">
                  <select className="input" value={method} onChange={(e) => setMethod(e.target.value)}>
                    {["POST", "GET", "PUT", "PATCH", "DELETE"].map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Path" hint={urlPreview ? `→ ${urlPreview}` : "Relative to the service base URL."}>
                  <input className="input" value={path} onChange={(e) => setPath(e.target.value)} placeholder="/jobs/report" />
                </Field>
              </div>
            </div>
          )}

          {targetType === "group" && (
            <Field label="Target group ID">
              <input className="input" value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)} />
            </Field>
          )}
        </Card>

        <Card title="Schedule">
          <Field label="Kind">
            <select
              className="input"
              value={scheduleKind}
              onChange={(e) => setScheduleKind(e.target.value as ScheduleKind)}
            >
              <option value="cron">cron</option>
              <option value="interval">interval</option>
              <option value="one_shot">one-shot</option>
            </select>
          </Field>

          {scheduleKind === "cron" && (
            <Field
              label="Cron expression"
              hint="5 fields = minute precision (tick engine). 6 fields = seconds precision (do_alarm engine)."
            >
              <input className="input font-mono" value={cron} onChange={(e) => setCron(e.target.value)} />
            </Field>
          )}

          {scheduleKind === "interval" && (
            <Field label="Interval (seconds)" hint="Below 60s runs on the do_alarm engine.">
              <input
                className="input font-mono"
                type="number"
                min={1}
                value={intervalSeconds}
                onChange={(e) => setIntervalSeconds(Number(e.target.value))}
              />
            </Field>
          )}

          {scheduleKind === "one_shot" && (
            <Field label="Not before" hint="The single fire time (local browser time, sent as epoch-ms).">
              <input
                className="input font-mono"
                type="datetime-local"
                value={notBefore}
                onChange={(e) => setNotBefore(e.target.value)}
              />
            </Field>
          )}

          <Field label="Timezone">
            <select className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {COMMON_TZ.map((tz) => (
                <option key={tz}>{tz}</option>
              ))}
            </select>
          </Field>

          {subMinute && scheduleKind !== "one_shot" && (
            <div className="rounded border border-accent/40 bg-accent/10 p-2 text-xs text-accent">
              Sub-minute cadence detected → this task will be promoted to the <span className="font-mono">do_alarm</span>{" "}
              engine (Durable Object alarm scheduler).
            </div>
          )}
        </Card>

        <Card title="Payload & callbacks">
          <Field label="Payload (JSON)" hint={payloadError ?? "Sent as the request body. Use Secrets Store refs for secrets."}>
            <textarea
              className={`input h-32 font-mono ${payloadError ? "border-danger" : ""}`}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
            />
          </Field>
          <Field label="Success callback URL (optional)">
            <input className="input font-mono" value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)} />
          </Field>
          <Field label="Failure callback URL (optional)">
            <input
              className="input font-mono"
              value={failureCallbackUrl}
              onChange={(e) => setFailureCallbackUrl(e.target.value)}
            />
          </Field>
        </Card>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={() => navigate("/tasks")}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Publishing…" : "Publish task"}
          </button>
        </div>
      </form>
    </div>
  );
}